import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { format, formatDistanceToNowStrict } from "date-fns"
import { ru } from "date-fns/locale/ru"
import { ChevronRight, Megaphone, Plus } from "lucide-react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import {
	BroadcastProgress,
	BroadcastStatusBadge,
	type BroadcastSummary,
	broadcastKeys,
	createBroadcast,
	formatWhen,
	listBroadcasts,
	processedCount,
	sentCount,
} from "@/entities/broadcast"
import { useSessionRole } from "@/entities/session"
import { AccessDenied } from "@/shared/ui/access-denied"
import { Button } from "@/shared/ui/button"
import { Card } from "@/shared/ui/card"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { Skeleton } from "@/shared/ui/skeleton"
import { WorkspaceSection } from "@/widgets/bot-workspace"
import { LoadSwap } from "@/shared/ui/load-swap"
import { AnimatePresence, motion } from "framer-motion"
import { listItem } from "@/shared/lib/motion"
import { Reveal } from "@/shared/ui/reveal"

const TITLE = "Рассылки"
/** Ещё не ушедшие или уходящие прямо сейчас: их показываем первыми. */
const OPEN_STATUSES = new Set(["DRAFT", "SCHEDULED", "SENDING"])

function summaryLine(broadcast: BroadcastSummary): string {
	const { stats } = broadcast
	switch (broadcast.status) {
		case "DRAFT":
			return `изменена ${formatDistanceToNowStrict(new Date(broadcast.updatedAt), { addSuffix: true, locale: ru })}`
		case "SCHEDULED":
			return broadcast.scheduledAt
				? `отправка ${formatWhen(broadcast.scheduledAt)}`
				: "запланирована"
		case "SENDING":
			return `отправлено ${processedCount(stats)} из ${stats.total}`
		case "DONE":
			return `отправлено ${sentCount(stats)} из ${stats.total}${broadcast.finishedAt ? ` · ${formatWhen(broadcast.finishedAt)}` : ""}`
		case "CANCELLED":
			return `остановлена · отправлено ${sentCount(stats)} из ${stats.total}`
	}
}

/** Рассылки бота: черновики, запланированные и отправленные со статистикой. */
export function BroadcastsPage() {
	const { botId = "" } = useParams()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { isModerator } = useSessionRole()

	const list = useQuery({
		queryKey: broadcastKeys.list(botId),
		queryFn: () => listBroadcasts(botId),
		enabled: isModerator && !!botId,
		refetchInterval: (query) =>
			query.state.data?.some((item) => item.status === "SENDING" || item.status === "SCHEDULED")
				? 5000
				: false,
	})
	const create = useMutation({
		mutationFn: () =>
			createBroadcast(botId, { name: `Рассылка ${format(new Date(), "d MMMM", { locale: ru })}` }),
		onSuccess: async (created) => {
			queryClient.setQueryData(broadcastKeys.detail(botId, created.id), created)
			await queryClient.invalidateQueries({ queryKey: broadcastKeys.list(botId) })
			navigate(`/admin/bots/${botId}/broadcasts/${created.id}`)
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось создать рассылку")),
	})

	if (!isModerator) return <AccessDenied title={TITLE} />
	const open = list.data?.filter((item) => OPEN_STATUSES.has(item.status)) ?? []
	const sent = list.data?.filter((item) => !OPEN_STATUSES.has(item.status)) ?? []
	const newButton = (
		<Button className="rounded-xl" disabled={create.isPending} onClick={() => create.mutate()}>
			<Plus />
			Новая рассылка
		</Button>
	)

	return (
		<WorkspaceSection
			title={TITLE}
			subtitle="Сообщение пользователям бота: всем или по фильтру, сейчас или по расписанию"
			actions={list.data?.length ? newButton : undefined}
		>
			<LoadSwap
				loading={list.isLoading}
				id={list.isError ? "error" : list.data?.length ? "list" : "empty"}
			>
				{list.isLoading ? (
					<BroadcastsSkeleton />
				) : list.isError ? (
					<LoadErrorState onRetry={() => void list.refetch()} />
				) : !list.data?.length ? (
					<Card className="flex flex-col items-center gap-4 border-dashed border-foreground/15 bg-card/40 px-6 py-12 text-center">
						<Megaphone className="h-8 w-8 text-muted-foreground" />
						<p className="max-w-md text-muted-foreground">
							Рассылок пока не было. Рассылка сохраняется черновиком, пока вы её не отправите.
						</p>
						{newButton}
					</Card>
				) : (
					<div>
						<BroadcastGroup title="В работе" items={open} botId={botId} className="pb-6" />
						<BroadcastGroup title="Отправлены" items={sent} botId={botId} />
					</div>
				)}
			</LoadSwap>
		</WorkspaceSection>
	)
}

/** Заглушка списка: заголовок группы и строки со значком, названием, итогом и полосой. */
function BroadcastsSkeleton() {
	return (
		<div aria-hidden="true" className="space-y-2">
			<div className="flex h-5 items-center">
				<Skeleton className="h-3.5 w-24 rounded" />
			</div>
			<Card className="divide-y divide-border/60 overflow-hidden border-border/60">
				{[0, 1, 2].map((index) => (
					<div key={index} className="flex items-center gap-3 px-4 py-3">
						<Skeleton className="size-9 shrink-0 rounded-xl" />
						<div className="min-w-0 flex-1 space-y-1">
							<div className="flex h-6 items-center gap-2">
								<Skeleton className="h-4 w-48 rounded" />
								<Skeleton className="h-5 w-20 rounded-full" />
							</div>
							<div className="flex h-4 items-center">
								<Skeleton className="h-3 w-64 max-w-full rounded" />
							</div>
						</div>
						<Skeleton className="size-4 shrink-0 rounded" />
					</div>
				))}
			</Card>
		</div>
	)
}

function BroadcastGroup({
	title,
	items,
	botId,
	className,
}: {
	title: string
	items: BroadcastSummary[]
	botId: string
	/** Отступ до следующей группы: внутри, чтобы пропадал вместе с группой. */
	className?: string
}) {
	const navigate = useNavigate()
	return (
		<Reveal show={items.length > 0} className={className}>
			<section className="space-y-2">
				<h2 className="text-sm font-medium text-muted-foreground">
					{title} <span className="tabular-nums">{items.length}</span>
				</h2>
				<Card className="overflow-hidden border-border/60">
					<ul className="divide-y divide-border/60">
						{/* Рассылка, которая ушла или появилась, сворачивается и раскрывается по высоте. */}
						<AnimatePresence initial={false}>
							{items.map((broadcast) => (
								<motion.li key={broadcast.id} {...listItem}>
									<Button
										type="button"
										variant="ghost"
										onClick={() => navigate(`/admin/bots/${botId}/broadcasts/${broadcast.id}`)}
										className="h-auto w-full justify-start gap-3 rounded-none px-4 py-3 text-left hover:bg-secondary/50"
									>
										<span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-violet-400/15 text-violet-300">
											<Megaphone className="h-[18px] w-[18px]" />
										</span>
										<div className="min-w-0 flex-1 space-y-1">
											<div className="flex min-w-0 items-center gap-2">
												<span className="truncate font-medium">{broadcast.name}</span>
												<BroadcastStatusBadge status={broadcast.status} />
											</div>
											<p className="truncate text-xs text-muted-foreground">
												{summaryLine(broadcast)}
											</p>
											{broadcast.stats.total > 0 ? (
												<BroadcastProgress stats={broadcast.stats} />
											) : null}
										</div>
										<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
									</Button>
								</motion.li>
							))}
						</AnimatePresence>
					</ul>
				</Card>
			</section>
		</Reveal>
	)
}
